import React, { useState, useEffect } from 'react';
import { X, Users, FileText, Clock, MapPin, User, Save, Edit2, UserCog, Loader } from 'lucide-react';
import api from '../api';
import Swal from 'sweetalert2';

const GroupeDetail = ({ groupe, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [groupeStats, setGroupeStats] = useState(null);
  const [membres, setMembres] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [editData, setEditData] = useState({
    titre: groupe.titre,
    description: groupe.description || '',
    region: groupe.region || ''
  });

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchGroupeStats();
    } else if (activeTab === 'membres') {
      fetchMembres();
    } else if (activeTab === 'demandes') {
      fetchDemandes();
    }
  }, [activeTab]);

  const fetchGroupeStats = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/utilisateur/admin/groupes/${groupe.id}/stats/`);
      setGroupeStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembres = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/utilisateur/admin/groupes/${groupe.id}/membres/`);
      setMembres(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des membres:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDemandes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/utilisateur/admin/groupes/${groupe.id}/demandes/`);
      setDemandes(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    onUpdate(groupe.id, editData);
    setIsEditing(false);
  };

  const handleRemoveMembre = async (membreId) => {
    const result = await Swal.fire({
      title: 'Retirer ce membre ?',
      text: 'Cette action retirera le membre du groupe',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, retirer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/utilisateur/admin/groupes/${groupe.id}/membres/`, {
          data: { membre_id: membreId }
        });
        await Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: 'Membre retiré avec succès',
          confirmButtonColor: '#FF6B00',
        });
        fetchMembres();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de retirer le membre',
          confirmButtonColor: '#FF6B00',
        });
      }
    }
  };

  const handleDemandeAction = async (demandeId, action) => {
    try {
      await api.post(`/api/utilisateur/admin/groupes/${groupe.id}/demandes/${demandeId}/`, {
        action
      });
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: `Demande ${action === 'accepter' ? 'acceptée' : 'refusée'} avec succès`,
        confirmButtonColor: '#FF6B00',
      });
      fetchDemandes();
      if (action === 'accepter') {
        fetchMembres();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de traiter la demande',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-900">Détails du Groupe</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            {['info', 'stats', 'membres', 'demandes'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#FF6B00] text-[#FF6B00]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'info' && 'Informations'}
                {tab === 'stats' && 'Statistiques'}
                {tab === 'membres' && `Membres (${groupe.membres_count})`}
                {tab === 'demandes' && `Demandes (${groupe.demandes_count})`}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {!isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                    <p className="text-gray-900">{groupe.titre}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <p className="text-gray-900">{groupe.description || 'Aucune description'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Région</label>
                    <p className="text-gray-900">{groupe.region || 'Non spécifiée'}</p>
                  </div>
                  {groupe.admin_info && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Administrateur</label>
                      <div className="flex items-center gap-3">
                        <img
                          src={groupe.admin_info.image || '/default-avatar.png'}
                          alt={groupe.admin_info.username}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{groupe.admin_info.full_name}</p>
                          <p className="text-sm text-gray-500">@{groupe.admin_info.username}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55f00] transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Modifier
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                    <input
                      type="text"
                      value={editData.titre}
                      onChange={(e) => setEditData({ ...editData, titre: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Région</label>
                    <input
                      type="text"
                      value={editData.region}
                      onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Sauvegarder
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'stats' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader className="w-8 h-8 text-[#FF6B00] animate-spin" />
                </div>
              ) : groupeStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Membres */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="w-6 h-6 text-blue-600" />
                      <h3 className="text-lg font-semibold text-blue-900">Membres</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Total</span>
                        <span className="font-bold text-blue-900">{groupeStats.membres.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Actifs</span>
                        <span className="font-bold text-blue-900">{groupeStats.membres.actifs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Nouveaux (7j)</span>
                        <span className="font-bold text-blue-900">{groupeStats.membres.nouveaux_semaine}</span>
                      </div>
                    </div>
                  </div>

                  {/* Publications */}
                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <FileText className="w-6 h-6 text-green-600" />
                      <h3 className="text-lg font-semibold text-green-900">Publications</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-green-700">Total</span>
                        <span className="font-bold text-green-900">{groupeStats.publications.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-700">Cette semaine</span>
                        <span className="font-bold text-green-900">{groupeStats.publications.cette_semaine}</span>
                      </div>
                    </div>
                  </div>

                  {/* Demandes */}
                  <div className="bg-orange-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Clock className="w-6 h-6 text-orange-600" />
                      <h3 className="text-lg font-semibold text-orange-900">Demandes</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-orange-700">En attente</span>
                        <span className="font-bold text-orange-900">{groupeStats.demandes.en_attente}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-700">Acceptées</span>
                        <span className="font-bold text-orange-900">{groupeStats.demandes.acceptees}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-700">Refusées</span>
                        <span className="font-bold text-orange-900">{groupeStats.demandes.refusees}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-10">Aucune statistique disponible</p>
              )}
            </div>
          )}

          {activeTab === 'membres' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader className="w-8 h-8 text-[#FF6B00] animate-spin" />
                </div>
              ) : membres.length > 0 ? (
                <div className="space-y-3">
                  {membres.map((membre) => (
                    <div key={membre.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <img
                          src={membre.user_info.image || '/default-avatar.png'}
                          alt={membre.user_info.username}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{membre.user_info.full_name}</p>
                          <p className="text-sm text-gray-500">@{membre.user_info.username}</p>
                          <p className="text-xs text-gray-400">
                            Membre depuis {new Date(membre.date_rejoint).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMembre(membre.id)}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-10">Aucun membre</p>
              )}
            </div>
          )}

          {activeTab === 'demandes' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader className="w-8 h-8 text-[#FF6B00] animate-spin" />
                </div>
              ) : demandes.length > 0 ? (
                <div className="space-y-3">
                  {demandes.map((demande) => (
                    <div key={demande.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <img
                          src={demande.expediteur_info.image || '/default-avatar.png'}
                          alt={demande.expediteur_info.username}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{demande.expediteur_info.full_name}</p>
                          <p className="text-sm text-gray-500">@{demande.expediteur_info.username}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(demande.date_demande).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDemandeAction(demande.id, 'accepter')}
                          className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                        >
                          Accepter
                        </button>
                        <button
                          onClick={() => handleDemandeAction(demande.id, 'refuser')}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
                        >
                          Refuser
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-10">Aucune demande en attente</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupeDetail;
